import React, { useState, useRef, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import {
  createItemAction,
  updateItemAction,
  deleteItemAction,
} from "../../../redux/actions/careplanActions";
import styled from "styled-components";
import FullScreenOverlay from "../../common/FullScreenOverlay";
import IconButton from "../../common/Button";
import AddItemForm from "./AddItem/AddItemForm";
import EditItemForm from "./EditItemForm";
import ItemAuditLogs from "../AuditLog/ItemAuditLogs";
import { formatDate } from "../../../utils/dateUtils";
import ViewItem from "./ViewItem";
import {
  EyeIcon,
  EditIcon,
  LogIcon,
  DeleteIcon,
  PlusIcon,
} from "../../common/Icons";
import Spacer from "../../common/Spacer";
import Menu from "../../common/Menu";
import ItemThumbnail from "../../common/ItemThumbnail";
import SearchBox from "../../common/SearchBox";
import TextField from "../../common/TextField";

const ItemsList = ({ items }) => {
  const [showAddItemForm, setShowAddItemForm] = useState(false);
  const [formTitle, setFormTitle] = useState(""); // Dynamic title for modal
  const [modalButtons, setModalButtons] = useState([]); // Dynamic buttons
  const [formError, setFormError] = useState("");
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditItemForm, setShowEditItemForm] = useState(false);
  const [showAuditLogs, setShowAuditLogs] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [filteredItems, setFilteredItems] = useState(items);
  const [deleteText, setDeleteText] = useState("");

  const dispatch = useDispatch();
  const addItemFormRef = useRef();
  const editItemFormRef = useRef();

  // Update this useEffect to keep filteredItems in sync when items prop changes
  useEffect(() => {
    setFilteredItems(items);
  }, [items]);

  const sortedItems = useMemo(
    () => filteredItems.slice().sort((a, b) => a.title.localeCompare(b.title)),
    [filteredItems]
  );

  const handleAddItemClick = () => {
    setFormTitle("Add Item");
    setModalButtons([
      {
        label: "Cancel",
        onClick: handleFormCancel,
        variant: "secondary",
        state: "idle",
      },
      { label: "Save", onClick: handleAddFormSubmit, variant: "primary" }, // Primary styled Save button
    ]);
    setShowAddItemForm(true);
  };

  const handleFormCancel = () => {
    setShowAddItemForm(false);
    setShowEditItemForm(false);
    setShowViewModal(false);
    setFormError("");
    setSelectedItem(null);
  };

  const handleAddFormSubmit = () => {
    if (addItemFormRef.current) {
      addItemFormRef.current.requestSubmit();
    }
  };

  const handleEditFormSubmit = () => {
    if (editItemFormRef.current) {
      editItemFormRef.current.requestSubmit();
    }
    setIsEditMode(false); // Exit edit mode after submission
  };

  const handleAddFormSave = (newItem) => {
    const formData = new FormData();
    formData.append("title", newItem.title);
    formData.append("description", newItem.description);
    // Only append the image if it has been changed (and exists)
    if (newItem.imageChanged && newItem.imageFile) {
      formData.append("image", newItem.imageFile);
    }
    formData.append("image_alt_text", newItem.image_alt_text);
    formData.append("prompt_text", newItem.prompt_text);
    formData.append(
      "section_answers",
      JSON.stringify(newItem.section_answers || {})
    );
    formData.append(
      "prompt_overrides",
      JSON.stringify(newItem.section_prompts || {})
    );
    formData.append("resource_link", newItem.resource_link);
    formData.append(
      "hide_from_service_user_specific",
      newItem.hide_from_service_user_specific || false
    );

    dispatch(
      createItemAction(formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    );
    setShowAddItemForm(false); // Hide the form after submission
  };

  const handleEditFormSave = (updatedItem) => {
    const formData = new FormData();
    formData.append("title", updatedItem.title);
    formData.append("description", updatedItem.description);
    // Only append the image if it has been changed
    if (updatedItem.imageChanged && updatedItem.imageFile) {
      formData.append("image", updatedItem.imageFile);
    }
    formData.append("image_alt_text", updatedItem.image_alt_text);
    formData.append("prompt_text", updatedItem.prompt_text);
    formData.append(
      "section_answers",
      JSON.stringify(updatedItem.section_answers || {})
    );
    formData.append(
      "prompt_overrides",
      JSON.stringify(updatedItem.section_prompts || {})
    );
    formData.append("resource_link", updatedItem.resource_link);
    formData.append(
      "hide_from_service_user_specific",
      updatedItem.hide_from_service_user_specific || false
    );

    dispatch(updateItemAction({ id: updatedItem.id, formData })).then(() => {
      // Close the appropriate modal based on flow
      if (showViewModal) {
        setIsEditMode(false); // Return to view mode
      } else {
        setShowEditItemForm(false); // Close direct edit modal
      }
    });
  };

  // Function to handle the delete button click
  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteConfirmModal(true); // Show confirmation modal
  };

  // Function to handle delete confirmation
  const handleConfirmDelete = () => {
    const form = document.getElementById("deleteConfirmationForm"); // Select the form
    const deleteTextField = document.querySelector("input[name=deleteText]");
    // Check if the field is empty
    if (!deleteTextField.value.trim()) {
      deleteTextField.setCustomValidity("This field is required."); // Show empty field message
    } else if (deleteTextField.value !== "Delete") {
      deleteTextField.setCustomValidity(
        'Please type "Delete" to confirm deletion.'
      ); // Show custom message
    } else {
      deleteTextField.setCustomValidity(""); // Clear the custom validation message
    }

    // Trigger form validation
    if (form && !form.reportValidity()) {
      return; // Stop if the form is invalid
    }

    dispatch(deleteItemAction(itemToDelete.id))
      .then(() => {
        setDeleteText(""); // Clear the delete text
        setShowDeleteConfirmModal(false);
        setItemToDelete(null);

        // Close view modal if open
        if (showViewModal) {
          setShowViewModal(false);
        }

        // Reset selected item
        setSelectedItem(null);
      })
      .catch((error) => {
        console.error("Delete failed:", error);
      });
  };

  // Handle viewing an item
  const handleViewItem = (item) => {
    setSelectedItem(item);
    setIsEditMode(false); // Set to view mode
    setShowViewModal(true);
  };

  // Handle editing an item
  const handleEditItem = (item) => {
    setSelectedItem(item);

    // Determine which edit flow to use
    if (showViewModal) {
      // If already in view mode, switch to edit mode
      setIsEditMode(true);
    } else {
      // If coming from list, open edit form directly
      setShowEditItemForm(true);
      setFormTitle("Edit Item");
      setModalButtons([
        {
          label: "Cancel",
          onClick: () => setShowEditItemForm(false),
          variant: "secondary",
          state: "idle",
        },
        {
          label: "Save",
          onClick: handleEditFormSubmit,
          variant: "primary",
        },
      ]);
    }
  };

  const handleShowAuditLogs = (item) => {
    setSelectedItem(item);
    setShowViewModal(false); // First close view modal
    setShowAuditLogs(true); // Then open audit logs
  };

  const handleCloseAuditLogs = () => {
    setShowAuditLogs(false);
    // setShowViewModal(true); // Reopen view modal when closing audit logs
  };

  const handleSearch = (results) => {
    setFilteredItems(
      results.slice().sort((a, b) => a.title.localeCompare(b.title))
    );
  };

  // Popup Menu Items
  const menuItems = (item) => [
    {
      label: "View item",
      icon: <EyeIcon />,
      action: () => handleViewItem(item),
    },
    {
      label: "Edit item",
      icon: <EditIcon />,
      action: () => handleEditItem(item),
    },
    {
      label: "Audit log",
      icon: <LogIcon />,
      action: () => handleShowAuditLogs(item),
    },
    {
      label: "Delete item",
      icon: <DeleteIcon />,
      action: () => handleDeleteClick(item),
    },
  ];

  return (
    <>
      {showAuditLogs && (
        <ItemAuditLogs
          itemId={selectedItem.id}
          itemTitle={selectedItem.title}
          onClose={handleCloseAuditLogs}
        />
      )}
      {showAddItemForm && (
        <FullScreenOverlay
          title={formTitle} // Dynamic title
          buttons={modalButtons} // Dynamic buttons
          width={"100%"}
          height={"100%"}
        >
          <AddItemForm
            ref={addItemFormRef}
            onSave={handleAddFormSave}
            onCancel={handleFormCancel}
            formError={formError}
          />
        </FullScreenOverlay>
      )}
      {showEditItemForm && (
        <FullScreenOverlay
          title={formTitle} // Dynamic title
          buttons={modalButtons} // Dynamic buttons
          width={"100%"}
          height={"100%"}
        >
          <EditItemForm
            ref={editItemFormRef}
            item={selectedItem}
            onUpdate={handleEditFormSave}
            onCancel={handleFormCancel}
            formError={formError}
          />
        </FullScreenOverlay>
      )}
      {showViewModal && (
        <ViewItem
          selectedItem={selectedItem}
          onClose={() => {
            setShowViewModal(false);
            setIsEditMode(false);
          }}
          isEditMode={isEditMode} // Pass whether it's view or edit mode
          onEdit={() => setIsEditMode(true)}
          onSave={handleEditFormSubmit}
          onUpdate={handleEditFormSave}
          onCancel={() => setIsEditMode(false)}
          modalButtons={modalButtons} // Dynamic buttons based on mode
          onDeleteItem={handleDeleteClick}
          onShowAuditLogs={handleShowAuditLogs}
          editFormRef={editItemFormRef}
        />
      )}
      <SearchAddContainer>
        <SearchWrapper>
          <SearchBox
            placeholder="Search items"
            onSearch={handleSearch}
            searchFields={["title", "description"]}
            data={items}
          />
          {/* <TextField placeholder="Search here" /> */}
        </SearchWrapper>
        <AddButtonWrapper>
          <IconButton
            variant="primary"
            state="idle"
            icon={<PlusIcon color="#FFF" />}
            type="button"
            width={"auto"}
            onClick={handleAddItemClick}
          >
            Add new item
          </IconButton>
        </AddButtonWrapper>
      </SearchAddContainer>
      {sortedItems.length === 0 ? (
        <div>No items available</div>
      ) : (
        <ListContainer>
          {sortedItems.map((item) => (
            <ItemCard
              key={item.id}
              onClick={() => handleViewItem(item)}
              clickable={true}
            >
              <ItemThumbnail
                src={item?.image_url}
                alt={item?.image_alt_text || item?.title}
                width={"150px"}
                height={"150px"}
              />
              <ItemContent>
                <ItemTopContent>
                  <ItemTitleWrapper>
                    <ItemTitle>{item.title}</ItemTitle>
                    <div onClick={(e) => e.stopPropagation()}>
                      <Menu menuItems={menuItems(item)} />
                    </div>
                  </ItemTitleWrapper>

                  <ItemDescription>{item.description}</ItemDescription>
                </ItemTopContent>
                <ItemBottomContent>
                  <ItemMeta>
                    <UpdatedBy>
                      Last updated by{" "}
                      <UpdatedByName>{item.updated_by_name}</UpdatedByName>
                    </UpdatedBy>
                    <UpdatedAt>
                      Last updated{" "}
                      <UpdatedAtDate>
                        {formatDate(item.last_updated_at)}
                      </UpdatedAtDate>
                    </UpdatedAt>
                  </ItemMeta>
                </ItemBottomContent>
              </ItemContent>
            </ItemCard>
          ))}
          <Spacer />
        </ListContainer>
      )}

      {showDeleteConfirmModal && (
        <FullScreenOverlay
          width="20%"
          height="20%"
          isPopup={true}
          popupType="delete"
          buttons={[
            {
              label: "No, Cancel",
              onClick: () => {
                setShowDeleteConfirmModal(false);
                setItemToDelete(null);
                setDeleteText("");
              },
            },
            {
              label: "Yes, Delete",
              onClick: handleConfirmDelete, // Handle delete confirmation
              variant: "danger",
              state: "idle",
            },
          ]}
        >
          <h5 className="semi-bold">Confirm delete item</h5>
          <p>
            Are you sure you want to delete this item{" "}
            <span className="confirm-delete-nhs">{itemToDelete.title}</span>{" "}
            This action cannot be undone and the item will be permanently
            deleted.
          </p>
          <p>
            Type <b>"Delete"</b> to confirm
          </p>
          <form
            id="deleteConfirmationForm"
            onSubmit={(e) => e.preventDefault()}
          >
            <TextField
              placeholder=""
              maxLength={10}
              showCharacterLimit={false}
              name="deleteText"
              value={deleteText}
              onChange={(e) => {
                setDeleteText(e.target.value);
                e.target.setCustomValidity(""); // Clear custom validation message
              }}
              autoComplete="off" // otherwise the Delete will show in autocomplete which is not the good behaviour here
              required
            />
          </form>
        </FullScreenOverlay>
      )}
    </>
  );
};

const SearchAddContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
`;

const SearchWrapper = styled.div`
  width: 404px;
`;

const AddButtonWrapper = styled.div`
  // width: 170px;
`;

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px 0;
`;

const ItemCard = styled.div`
  display: flex;
  align-items: flex-start;
  background-color: #ffffff;
  border-radius: 12px;
  padding: 8px;
  border: 1px solid #e9ecef;
  ${({ clickable }) =>
    clickable &&
    `
      cursor: pointer;
      &:hover {
        box-shadow: 0px 5px 15px 0px rgba(0, 0, 0, 0.1);
        transition: box-shadow 0.3s ease;
      }
    `}
`;

const ItemContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-left: 8px;
`;

const ItemTopContent = styled.div`
  padding: 8px;
`;

const ItemBottomContent = styled.div`
  padding-left: 8px;
  padding-right: 8px;
  margin-top: 20px;
`;

const ItemTitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ItemTitle = styled.div`
  font-size: 20px;
  font-weight: 500;
  line-height: 24px;
  color: #161718;
`;

const ItemDescription = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  color: #6c757d;
  margin-top: 4px;
`;

const ItemMeta = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  color: #6c757d;
`;

const UpdatedBy = styled.span``;

const UpdatedByName = styled.span`
  font-weight: 500;
  color: #495057;
`;

const UpdatedAt = styled.span``;

const UpdatedAtDate = styled.span`
  font-weight: 500;
  color: #495057;
`;

// const FormTitle = styled.h5``;

export default ItemsList;
